/**
 * Module dependencies.
 */

import { AnimatedTypography } from 'components/atoms/animated-typography/animated-typography';
import { Image as ImageComponent } from 'components/atoms/image/image';
import { Img } from 'components/atoms/image/image.style';
import { SubTitle } from 'components/atoms/sub-title/sub-title';
import { color } from 'styles/utils/colors';
import { ifProp } from 'styled-tools';
import styled from '@emotion/styled';

/**
 * Export `Wrapper` styled component.
 */

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 64px 0 200px;
  cursor: pointer;
`;

/**
 * Export `Image` styled component.
 */

export const Image = styled(ImageComponent)`
  grid-area: image;
  width: 100%;
  height: 100%;
  padding-top:;

  @media (max-width: 640px) {
    height: 300px;
  }

  @media (max-width: 480px) {
    height: 200px;
  }
`;

/**
 * Export `CategoryWrapper` styled component.
 */

export const CategoryWrapper = styled.div<{
  invert: boolean;
}>`
  flex: 0.5;
  justify-content: center;
  overflow: hidden;
  align-items: start;
  display: grid;
  position: relative;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: min-content 1fr;
  grid-template-areas:
    'image title'
    'image description';

  ${ifProp('invert', 'grid-template-areas: "title image" "description image";')}

  cursor: pointer;

  ${Img} {
    transition: all 0.6s ease-in-out;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 100%;
    bottom: 0;
    background-color: ${color('primary100')};
    transition: all 0.45s ease-in-out;

    ${(props) => props.invert && `right: 0; left: 100%;`}
  }

  &:hover {
    z-index: 100;
    position: relative;

    &:before {
      ${ifProp('invert', 'left: 0;', 'right: 0;')}
    }

    ${Img} {
      transform: scale(1.05);
    }

    @media (max-width: 768px) {
      transform: initial;
    }
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'image'
      'title'
      'description';
  }
`;

/**
 * Export `Title` styled component.
 */

export const Title = styled(SubTitle)<{
  invert: boolean;
}>`
  grid-area: title;
  padding: 0 32px;
  margin-top: 64px;
  font-family: 'Inter', sans-serif;
  font-size: 48px;
  font-weight: 200;
  color: ${color('primary500')};

  @media (max-width: 1024px) {
    margin-top: 16px;
  }

  @media (max-width: 768px) {
    margin-top: 16px;
    font-size: 32px;
  }

  @media (max-width: 640px) {
    padding: initial;
    font-size: 24px;
    font-weight: 500;
  }
`;

/**
 * Export `Description` styled component.
 */

export const Description = styled(AnimatedTypography)<{
  invert: boolean;
}>`
  grid-area: description;
  margin: 0 32px;
  margin-top: 32px;
  margin-bottom: 32px;

  @media (min-width: 1024px) {
    min-height: 240px;
  }

  @media (max-width: 640px) {
    margin: initial;
    margin-top: 16px;
    margin-bottom: 64px;
  }
`;
